import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";

/* ---------- Helpers ---------- */
const seasonLabel = (d = new Date()) => {
  const y = d.getFullYear();
  const m = d.getMonth(); // 0..11
  const start = m >= 8 ? y : y - 1; // saison Sep (8) -> Août (7)
  return `${start}-${start + 1}`;
};

const TYPES = [
  "loisir",
  "ceinture",
  "poids",
  "renforcement",
  "competition",
  "kata",
  "uv2",
  "uv3_points",
  "autre",
];

/* ---------- Modal ---------- */
export default function CreateEditObjectifModal({
  open,
  onClose,
  onSubmit, // (payload, idToUpdate) => Promise
  initial, // objectif existant ou null
  adherentId, // requis
}) {
  const isEdit = !!initial?.id;
  const dialogRef = useRef(null);

  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [saison, setSaison] = useState(seasonLabel());
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Hydrate champs à l'ouverture
  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || "");
    setType(initial?.type || "");
    setSaison(initial?.saison || seasonLabel());
    setDescription(initial?.description || "");
    setStartDate(initial?.start_date?.slice?.(0, 10) || "");
    setDueDate(initial?.due_date?.slice?.(0, 10) || "");
    setProgress(typeof initial?.progress === "number" ? initial.progress : 0);
    setIsDone(!!initial?.is_done);
  }, [open, initial]);

  // Lock scroll + ESC + focus
  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);

    // focus first input
    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='title']")?.focus();
    }, 0);

    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      root.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Sync progress <-> done comme côté back
  const onProgressChange = (v) => {
    const n = Math.max(0, Math.min(100, Number(v) || 0));
    setProgress(n);
    if (n >= 100) setIsDone(true);
  };
  const onDoneToggle = (checked) => {
    setIsDone(checked);
    if (checked && progress < 100) setProgress(100);
    if (!checked && progress === 100) setProgress(99);
  };

  const canSubmit = useMemo(() => {
    return !!adherentId && title.trim().length > 0 && !submitting;
  }, [adherentId, title, submitting]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) {
      if (!title.trim()) toast.error("Le titre est requis.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        adherent_id: adherentId,
        title: title.trim(),
        type: type || null,
        saison: saison || null,
        description: description || null,
        start_date: startDate || new Date().toISOString().slice(0, 10),
        due_date: dueDate || new Date().toISOString().slice(0, 10),
        progress: Number(progress) || 0,
        is_done: !!isDone,
      };

      // 🔎 LOG bien lisible dans la console
      const endpoint = initial?.id
        ? `/api/objectifs/update/${initial.id}`
        : `/api/objectifs/create`;

      console.groupCollapsed(
        `%c[Objectifs] handleSubmit -> ${initial?.id ? "EDIT" : "CREATE"}`,
        "color:#7c3aed;font-weight:bold"
      );
      console.log("Endpoint:", endpoint);
      console.log("Adherent ID:", adherentId);
      console.log("Payload:", payload);
      console.groupEnd();

      await onSubmit(payload, initial?.id);
      handleClose();
    } catch (err) {
      console.error("❌ Erreur handleSubmit Objectif:", err);
      toast.error("Échec de l’enregistrement de l’objectif.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // --- PORTAL ---
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Modifier un objectif" : "Créer un objectif"}
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-2xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Header sticky en dégradé */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              {isEdit ? "Modifier l’objectif" : "Créer un objectif"}
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Titre obligatoire. La saison est pré-remplie automatiquement.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps scrollable + Footer sticky dans le même <form> */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-6"
        >
          {/* Bloc 1 : Informations */}
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/70 dark:bg-slate-900/40">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
              Informations
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Titre <span className="text-pink-600">*</span>
                </label>
                <input
                  name="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex. Obtenir la ceinture verte"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Type (optionnel)
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                >
                  <option value="">—</option>
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Saison (optionnel)
                </label>
                <input
                  value={saison}
                  onChange={(e) => setSaison(e.target.value)}
                  placeholder="2025-2026"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Défaut : <span className="font-medium">{seasonLabel()}</span>
                </p>
              </div>

              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Description (optionnel)
                </label>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Détails, sous-objectifs, notes…"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                />
              </div>
            </div>
          </div>

          {/* Bloc 2 : Planification & suivi */}
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/70 dark:bg-slate-900/40">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
              Planification & suivi
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Début (optionnel)
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Échéance (optionnel)
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                />
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Progression
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Number(progress) || 0}
                  onChange={(e) => onProgressChange(e.target.value)}
                  className="w-full"
                />
                <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                  {progress}%
                </div>
              </div>

              <div className="flex items-center gap-3 mt-2">
                <input
                  id="doneChk"
                  type="checkbox"
                  className="w-4 h-4"
                  checked={isDone}
                  onChange={(e) => onDoneToggle(e.target.checked)}
                />
                <label htmlFor="doneChk" className="text-sm">
                  Terminé
                </label>
              </div>
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-2">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 px-4 py-2 inline-flex items-center gap-2"
                title="Annuler"
              >
                <FaTimes />
                Annuler
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className={`rounded-xl px-4 py-2 inline-flex items-center gap-2 text-white bg-violet-600 hover:bg-violet-700 ${
                  !canSubmit ? "opacity-60 pointer-events-none" : ""
                }`}
                title={isEdit ? "Enregistrer" : "Créer"}
              >
                <FaSave />
                {submitting
                  ? "Enregistrement…"
                  : isEdit
                  ? "Enregistrer"
                  : "Créer"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Styles locaux pour la scrollbar du contenu */}
      <style>{`
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.35);
        }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>,
    document.body
  );
}
